import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { cleanDataForAPI, convertNumericFields } from '../../utils/data-cleanup';
import BomStructureModal from '../../components/manufacturing/BomStructureModal';
import { usePermissions } from '../../hooks/usePermissions';
import { getApiErrorMessage } from '../../components/ui/FormControls';

interface BOMHeader {
  id: string;
  bomNumber: string;
  itemCode: string;
  itemId: string;
  plantId?: string;
  bomType: string;
  version: number;
  revision: string;
  status: string;
  baseQuantity: number;
  baseUoM: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  materialCost: number;
  laborCost: number;
  overheadCost: number;
  totalCost: number;
  scrapPercentage: number;
  yieldPercentage: number;
  description?: string;
  notes?: string;
  isActive: boolean;
  lines?: BOMLine[];
  createdAt: string;
  updatedAt: string;
}

interface BOMLine {
  id?: string;
  componentCode: string;
  componentId: string;
  quantity: number;
  uom: string;
  scrapPercentage: number;
  notes?: string;
  position: number;
}

const BOMType = {
  MANUFACTURING: 'manufacturing',
  ENGINEERING: 'engineering',
  SALES: 'sales',
  COSTING: 'costing',
};

const BOMStatus = {
  DRAFT: 'draft',
  APPROVED: 'approved',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  OBSOLETE: 'obsolete',
};

const BOMManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBOM, setSelectedBOM] = useState<BOMHeader | null>(null);
  // The BOM whose components and explosion are open
  const [structureId, setStructureId] = useState<string | null>(null);
  const { can } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'grouped-type' | 'grouped-status'>('grid');
  
  const queryClient = useQueryClient();

  // Fetch BOMs
  const { data: bomsData, isLoading } = useQuery({
    queryKey: ['bomHeaders', searchTerm, filterType, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterType) params.append('bomType', filterType);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await api.get(`/master-data/bom-headers?${params}`);
      return response.data;
    },
  });

  // Fetch grouped data by type
  const { data: groupedByType } = useQuery({
    queryKey: ['bomHeadersGroupedType'],
    queryFn: async () => {
      const response = await api.get('/master-data/bom-headers/grouped/type');
      return response.data;
    },
    enabled: viewMode === 'grouped-type',
  });

  // Fetch grouped data by status
  const { data: groupedByStatus } = useQuery({
    queryKey: ['bomHeadersGroupedStatus'],
    queryFn: async () => {
      const response = await api.get('/master-data/bom-headers/grouped/status');
      return response.data;
    },
    enabled: viewMode === 'grouped-status',
  });

  // Create/Update BOM
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      // Clean the data and convert numeric fields from strings to numbers
      let cleanData = cleanDataForAPI(data);
      
      // Convert string values to numbers for numeric fields
      const numericFields = [
        'baseQuantity', 'version', 'materialCost', 'laborCost', 'overheadCost', 
        'totalCost', 'scrapPercentage', 'yieldPercentage', 'lotSize', 
        'minLotSize', 'maxLotSize', 'alternativeRank'
      ];
      
      cleanData = convertNumericFields(cleanData, numericFields);
      // The components are kept in their own window; the header form must not send them back
      delete cleanData.lines;
      delete cleanData.revisions;
      
      if (selectedBOM) {
        return api.patch(`/master-data/bom-headers/${selectedBOM.id}`, cleanData);
      }
      return api.post('/master-data/bom-headers', cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomHeaders'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedType'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedStatus'] });
      toast.success(selectedBOM ? 'BOM updated successfully' : 'BOM created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Operation failed');
    },
  });

  // Delete BOM
  // A copy is a new draft BOM with the same lines, at the next version
  const copyMutation = useMutation({
    mutationFn: async ({ id, newBOMCode }: { id: string; newBOMCode: string }) =>
      api.post(`/master-data/bom-headers/${id}/copy`, { newBOMCode }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bomHeaders'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedType'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedStatus'] });
      toast.success(`${response.data?.bomNumber ?? 'The copy'} is saved as a draft`);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'The BOM could not be copied'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/master-data/bom-headers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomHeaders'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedType'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedStatus'] });
      toast.success('BOM deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Delete failed');
    },
  });

  // Toggle status
  const toggleStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/master-data/bom-headers/${id}/toggle-status`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bomHeaders'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedType'] });
      queryClient.invalidateQueries({ queryKey: ['bomHeadersGroupedStatus'] });
      toast.success('Status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Status update failed');
    },
  });

  const openModal = (bom?: BOMHeader) => {
    setSelectedBOM(bom || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBOM(null);
  };

  const getTypeIcon = (bomType: string) => {
    switch (bomType) {
      case BOMType.MANUFACTURING:
        return <CogIcon className="h-5 w-5" />;
      case BOMType.ENGINEERING:
        return <ClipboardDocumentListIcon className="h-5 w-5" />;
      case BOMType.SALES:
        return <CubeIcon className="h-5 w-5" />;
      case BOMType.COSTING:
        return <DocumentDuplicateIcon className="h-5 w-5" />;
      default:
        return <CubeIcon className="h-5 w-5" />;
    }
  };

  const getTypeColor = (bomType: string) => {
    const typeColors = {
      [BOMType.MANUFACTURING]: 'bg-blue-100 text-blue-800',
      [BOMType.ENGINEERING]: 'bg-green-100 text-green-800',
      [BOMType.SALES]: 'bg-purple-100 text-purple-800',
      [BOMType.COSTING]: 'bg-orange-100 text-orange-800',
    };

    return typeColors[bomType] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    const statusColors = {
      [BOMStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [BOMStatus.APPROVED]: 'bg-blue-100 text-blue-800',
      [BOMStatus.ACTIVE]: 'bg-green-100 text-green-800',
      [BOMStatus.INACTIVE]: 'bg-yellow-100 text-yellow-800',
      [BOMStatus.OBSOLETE]: 'bg-red-100 text-red-800',
    };

    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const renderBOMCard = (bom: BOMHeader) => (
    <Card key={bom.id} className="hover:shadow-lg transition-shadow">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {getTypeIcon(bom.bomType)}
            <div>
              <h3 className="font-semibold text-gray-900">{bom.bomNumber}</h3>
              <p className="text-sm text-gray-500">{bom.itemCode}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(bom.bomType)}`}>
              {bom.bomType}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bom.status)}`}>
              {bom.status}
            </span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Version:</span>
            <span className="font-medium">v{bom.version}.{bom.revision}</span>
          </div>
          
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Base Qty:</span>
            <span className="font-medium">{bom.baseQuantity} {bom.baseUoM}</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Cost:</span>
            <span className="font-medium">₹{bom.totalCost.toLocaleString()}</span>
          </div>
          
          {bom.description && (
            <div className="text-sm">
              <span className="text-gray-600">Description:</span>
              <p className="text-gray-800 mt-1 line-clamp-2">{bom.description}</p>
            </div>
          )}
          
          <div className="flex flex-wrap gap-1 mt-2">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
              {bom.lines?.length || 0} Components
            </span>
            {bom.scrapPercentage > 0 && (
              <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                {bom.scrapPercentage}% Scrap
              </span>
            )}
            {bom.yieldPercentage < 100 && (
              <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">
                {bom.yieldPercentage}% Yield
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <button
            onClick={() => toggleStatusMutation.mutate(bom.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
              bom.isActive
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {bom.isActive ? (
              <CheckCircleIcon className="h-4 w-4" />
            ) : (
              <XCircleIcon className="h-4 w-4" />
            )}
            {bom.isActive ? 'Active' : 'Inactive'}
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => setStructureId(bom.id)}
              className="p-1 hover:bg-blue-50 rounded"
              title="Components and explosion"
              aria-label={`Components and explosion of ${bom.bomNumber}`}
            >
              <EyeIcon className="h-4 w-4 text-blue-600" />
            </button>
            <button
              onClick={() => openModal(bom)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit BOM"
            >
              <PencilIcon className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={() => {
                const newBOMCode = window.prompt(`Number of the copy of ${bom.bomNumber}`, `${bom.bomNumber}-COPY`)?.trim();
                if (newBOMCode) copyMutation.mutate({ id: bom.id, newBOMCode });
              }}
              disabled={copyMutation.isPending}
              className="p-1 hover:bg-green-50 rounded"
              title="Copy BOM"
            >
              <DocumentDuplicateIcon className="h-4 w-4 text-green-600" />
            </button>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this BOM?')) {
                  deleteMutation.mutate(bom.id);
                }
              }}
              className="p-1 hover:bg-red-50 rounded"
              title="Delete BOM"
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">BOM Management</h1>
        <p className="text-gray-600">Manage Bill of Materials for manufacturing</p>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search BOMs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {Object.entries(BOMType).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Statuses</option>
              {Object.entries(BOMStatus).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-lg text-sm ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('grouped-type')}
                className={`px-3 py-2 rounded-lg text-sm ${
                  viewMode === 'grouped-type'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Type
              </button>
              <button
                onClick={() => setViewMode('grouped-status')}
                className={`px-3 py-2 rounded-lg text-sm ${
                  viewMode === 'grouped-status'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Status
              </button>
            </div>

            <div></div>
            <div></div>

            <Button onClick={() => openModal()} className="flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Add BOM
            </Button>
          </div>
        </div>
      </Card>

      {/* Content based on view mode */}
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bomsData?.data?.map((bom: BOMHeader) => renderBOMCard(bom))}
        </div>
      ) : viewMode === 'grouped-type' ? (
        <div className="space-y-6">
          {Object.entries(BOMType).map(([typeKey, typeValue]) => (
            <Card key={typeValue}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  {getTypeIcon(typeValue)}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {typeKey.charAt(0) + typeKey.slice(1).toLowerCase()}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(typeValue)}`}>
                    {groupedByType?.[typeValue]?.length || 0} BOMs
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedByType?.[typeValue]?.map((bom: BOMHeader) => renderBOMCard(bom))}
                </div>
                {(!groupedByType?.[typeValue] || groupedByType[typeValue].length === 0) && (
                  <p className="text-gray-500 text-center py-4">No BOMs found for this type</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(BOMStatus).map(([statusKey, statusValue]) => (
            <Card key={statusValue}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowPathIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {statusKey.charAt(0) + statusKey.slice(1).toLowerCase()}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(statusValue)}`}>
                    {groupedByStatus?.[statusValue]?.length || 0} BOMs
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedByStatus?.[statusValue]?.map((bom: BOMHeader) => renderBOMCard(bom))}
                </div>
                {(!groupedByStatus?.[statusValue] || groupedByStatus[statusValue].length === 0) && (
                  <p className="text-gray-500 text-center py-4">No BOMs found for this status</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <BomStructureModal bomId={structureId} canEdit={can('master_data.update')} onClose={() => setStructureId(null)} />

      {isModalOpen && (
        <BOMModal
          bom={selectedBOM}
          onClose={closeModal}
          onSubmit={(data) => createUpdateMutation.mutate(data)}
        />
      )}
    </div>
  );
};

// BOM Modal Component (simplified for now)
const BOMModal: React.FC<{
  bom: BOMHeader | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ bom, onClose, onSubmit }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<any>({
    defaultValues: bom || {
      bomType: BOMType.MANUFACTURING,
      status: BOMStatus.DRAFT,
      version: 1,
      revision: 'A',
      baseQuantity: 1,
      baseUoM: 'EA',
      scrapPercentage: 0,
      yieldPercentage: 100,
      alternativeRank: 1,
      materialCost: 0,
      laborCost: 0,
      overheadCost: 0,
      totalCost: 0,
      lotSize: 1,
      isActive: true,
    },
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {bom ? 'Edit BOM' : 'Add New BOM'}
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                BOM Number
              </label>
              <input
                {...register('bomNumber')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Auto-generated if empty"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item Code *
              </label>
              <input
                {...register('itemCode', { required: 'Item Code is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., PROD-001, ASSY-123"
              />
              {errors.itemCode && (
                <p className="text-red-500 text-sm mt-1">{errors.itemCode.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item ID *
              </label>
              <select
                {...register('itemId', { required: 'Item ID is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select an item...</option>
                {/* TODO: Load items from API */}
                <option value="550e8400-e29b-41d4-a716-446655440001">Sample Product A</option>
                <option value="550e8400-e29b-41d4-a716-446655440002">Sample Product B</option>
              </select>
              {errors.itemId && (
                <p className="text-red-500 text-sm mt-1">{errors.itemId.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plant (Optional)
              </label>
              <select
                {...register('plantId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">No plant selected</option>
                {/* TODO: Load plants from API */}
                <option value="550e8400-e29b-41d4-a716-446655440010">Main Plant</option>
                <option value="550e8400-e29b-41d4-a716-446655440011">Secondary Plant</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                BOM Type *
              </label>
              <select
                {...register('bomType', { required: 'BOM Type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(BOMType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status *
              </label>
              <select
                {...register('status', { required: 'Status is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(BOMStatus).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Quantity *
              </label>
              <input
                type="number"
                step="0.001"
                min="0.001"
                {...register('baseQuantity', { required: 'Base Quantity is required', min: 0.001 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base UoM *
              </label>
              <input
                {...register('baseUoM', { required: 'Base UoM is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="EA, KG, LTR, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scrap Percentage (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register('scrapPercentage')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yield Percentage (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register('yieldPercentage')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('isActive')}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label className="text-sm text-gray-700">Active</label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {bom ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BOMManagement;